import { useState, useEffect } from 'react';
import api from '../services/api';

export interface SystemSettings {
    PASSWORD_RESET_ENABLED?: string;
    CLEANUP_ENABLED?: string;
    CLEANUP_GRACE_PERIOD_DAYS?: string;
    CLEANUP_BACKUP_RETENTION_DAYS?: string;
    NEW_GUI_ENABLED?: string;
    WORKSPACE_CONTAINER_ENABLED?: string;
    MYWS_PORTAL_ENABLED?: string;
    [key: string]: string | undefined;
}

// Module-level cache so multiple components share one fetch per page load
let cachedSettings: SystemSettings | null = null;
let fetchPromise: Promise<SystemSettings> | null = null;

async function fetchSettings(): Promise<SystemSettings> {
    if (cachedSettings) return cachedSettings;
    if (fetchPromise) return fetchPromise;

    fetchPromise = api.get<Record<string, { value: string }>>('/api/system-settings')
        .then(({ data }) => {
            const flat: SystemSettings = {};
            for (const [k, v] of Object.entries(data)) flat[k] = v.value;
            cachedSettings = flat;
            fetchPromise = null;
            return flat;
        })
        .catch(() => {
            fetchPromise = null;
            return {} as SystemSettings;
        });

    return fetchPromise;
}

/** Invalidate the cache (call after saving a setting so the sidebar re-reads) */
export function invalidateSettingsCache(): void {
    cachedSettings = null;
    fetchPromise = null;
}

export function useSystemSettings(): { settings: SystemSettings; loading: boolean } {
    const [settings, setSettings] = useState<SystemSettings>(cachedSettings ?? {});
    const [loading, setLoading] = useState(!cachedSettings);

    useEffect(() => {
        let cancelled = false;
        fetchSettings().then(s => {
            if (!cancelled) { setSettings(s); setLoading(false); }
        });
        return () => { cancelled = true; };
    }, []);

    return { settings, loading };
}
